import { useState } from "react";
import {
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  LifeBuoy,
  RotateCcw,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/utils";
import { describeRetryProvisioning } from "@/lib/retryProvisioning";
import type { ResourceStatus } from "@/types/admin";

interface RetryProvisioningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  status: ResourceStatus;
  tenantName?: string | null;
  /** When the VM was ordered — roughly when the timed-out create request went out. */
  createdAt: string;
  /**
   * No provider id is recorded, so the last create request may have built a machine WiCloud never
   * heard back about (server-computed `machineMayExist`). The dialog then warns, and Retry waits for
   * the operator to confirm the infrastructure team found no machine.
   */
  mayAlreadyExist: boolean;
  /**
   * The provider id this VM records, shown beside the "machine is gone" option. That option is
   * offered whenever an id is recorded (`mayAlreadyExist` false), also when this is unknown here.
   */
  recordedVmUuid?: string | null;
  /**
   * Opens "Recover VM from infrastructure" instead — the answer when the machine does exist.
   * Optional: the list row action has no recovery target of its own (only the detail page does),
   * so that button is hidden there rather than sent nowhere.
   */
  onRecoverInstead?: () => void;
  /** True while the request runs; the dialog stays open and cannot be dismissed until it settles. */
  isPending: boolean;
  /**
   * `recreateMissingVm` is the operator's tick to drop a recorded machine the infrastructure no
   * longer has; always false for a VM with no provider id.
   */
  onConfirm: (options: { recreateMissingVm: boolean }) => void;
}

/**
 * Confirmation for "Retry provisioning". Says what the retry will do for this VM's status, with the
 * rules behind it one click away in "How this works" and the full guide in its own tab.
 */
export function RetryProvisioningDialog({
  open,
  onOpenChange,
  resourceName,
  status,
  tenantName,
  createdAt,
  mayAlreadyExist,
  recordedVmUuid,
  onRecoverInstead,
  isPending,
  onConfirm,
}: RetryProvisioningDialogProps) {
  // The page mounts this dialog only while it is open, so every opening starts unticked: a tick left
  // over from an earlier visit is not a check.
  const [confirmedNoMachine, setConfirmedNoMachine] = useState(false);
  const canRetry = !isPending && (!mayAlreadyExist || confirmedNoMachine);

  // A recorded machine is only re-checked. When it is gone the operator ticks this instead, and the
  // server drops the id (after asking the infrastructure itself) so a new machine is created.
  const hasRecordedMachine = !mayAlreadyExist;
  const [recreateMissingVm, setRecreateMissingVm] = useState(false);

  return (
    <Dialog
      open={open}
      // Escape and the corner X are ignored while the request runs, so the outcome is not reported
      // behind a dialog that already vanished.
      onOpenChange={(next) => {
        if (next || !isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Retry provisioning of {resourceName}?</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            {describeRetryProvisioning(status)}
          </DialogDescription>
          {mayAlreadyExist && (
            <PossibleMachineWarning
              resourceName={resourceName}
              tenantName={tenantName}
              createdAt={createdAt}
              confirmed={confirmedNoMachine}
              onConfirmedChange={setConfirmedNoMachine}
              onRecoverInstead={
                onRecoverInstead
                  ? () => {
                      onOpenChange(false);
                      onRecoverInstead();
                    }
                  : undefined
              }
              disabled={isPending}
            />
          )}
          {hasRecordedMachine && (
            <MissingMachineOption
              recordedVmUuid={recordedVmUuid}
              checked={recreateMissingVm}
              onCheckedChange={setRecreateMissingVm}
              disabled={isPending}
            />
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <HelpPopover />
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={!canRetry}
            onClick={() =>
              onConfirm({
                recreateMissingVm: hasRecordedMachine && recreateMissingVm,
              })
            }
          >
            {isPending ? (
              <Spinner className="mr-2 h-4 w-4" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )}
            Retry provisioning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PossibleMachineWarningProps {
  resourceName: string;
  tenantName?: string | null;
  createdAt: string;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  /** Absent on the list row action, which has no recovery target of its own — hides the button. */
  onRecoverInstead?: () => void;
  disabled: boolean;
}

/**
 * The one case a retry can build a second machine: no provider id is recorded, so the last create
 * request may have been carried out unseen, and a retry sends a new one. Says what to check, where to
 * go when the machine exists, and holds Retry until the operator confirms it does not.
 */
function PossibleMachineWarning({
  resourceName,
  tenantName,
  createdAt,
  confirmed,
  onConfirmedChange,
  onRecoverInstead,
  disabled,
}: PossibleMachineWarningProps) {
  const owner = tenantName ? `, tenant ${tenantName}` : "";

  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Check first: this VM may already exist</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          <b>No provider id is recorded</b> for this VM. Its last create request
          may still have been carried out — after a timeout, a dropped
          connection or a server error the provider can build the machine
          without WiCloud ever hearing back. A retry sends a <b>new</b> create
          request — if the machine exists, the customer ends up with <b>two</b>.
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            Ask the infrastructure team whether a machine exists for{" "}
            <b>{resourceName}</b> (
            {`requested ${formatDateTime(createdAt)}${owner}`}).
          </li>
          <li>
            <b>It exists</b>: do not retry. Link it with Recover VM from
            infrastructure
            {onRecoverInstead ? "" : " (open this VM's detail page)"}.
          </li>
          <li>
            <b>It does not</b>: tick the box below and retry.
          </li>
        </ol>
        {/* Absent from the list row action, which has no recovery target of its own — the list
            item above sends the operator to the detail page instead. */}
        {onRecoverInstead && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onRecoverInstead}
          >
            <LifeBuoy className="mr-2 h-4 w-4" />
            Recover VM from infrastructure instead
          </Button>
        )}
        <label className="flex cursor-pointer items-start gap-2 text-foreground">
          <Checkbox
            checked={confirmed}
            disabled={disabled}
            onCheckedChange={(value) => onConfirmedChange(value === true)}
            className="mt-0.5"
          />
          <span>
            The infrastructure team confirmed no machine exists for this VM.
          </span>
        </label>
      </AlertDescription>
    </Alert>
  );
}

interface MissingMachineOptionProps {
  recordedVmUuid?: string | null;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
}

/**
 * The other dead end: a provider id is recorded, so a retry only re-checks that machine, but the
 * infrastructure no longer has it and every check comes back "not found" until the retry times out.
 * Ticking this asks the server to drop the id and create a new machine. The server asks the
 * infrastructure itself first, so the tick alone never drops a machine that still exists.
 */
function MissingMachineOption({
  recordedVmUuid,
  checked,
  onCheckedChange,
  disabled,
}: MissingMachineOptionProps) {
  return (
    <Alert>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Is the recorded machine gone?</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          This VM records{" "}
          {recordedVmUuid ? (
            <>
              machine <code className="break-all">{recordedVmUuid}</code>
            </>
          ) : (
            "a provider id"
          )}
          , so a retry only re-checks that machine. If the infrastructure no
          longer has it, the retry checks for about 15 minutes and fails again.
        </p>
        <label className="flex cursor-pointer items-start gap-2 text-foreground">
          <Checkbox
            checked={checked}
            disabled={disabled}
            onCheckedChange={(value) => onCheckedChange(value === true)}
            className="mt-0.5"
          />
          <span>
            Drop this machine id and create a <b>new</b> machine. WiCloud asks
            the infrastructure first: the id is dropped only when it answers
            that the machine does not exist. If the machine is still there, or
            there is no answer, the retry is refused and nothing changes.
          </span>
        </label>
      </AlertDescription>
    </Alert>
  );
}

/**
 * What the retry does and when it refuses, opened in place. The long version, with the reason for
 * each refusal, is `/docs/retry-provisioning.html`.
 */
function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="sm:mr-auto">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="max-h-[70vh] w-96 space-y-3 overflow-y-auto text-sm"
      >
        <p className="text-muted-foreground">
          For a VM that never ran, on a new-service order, within{" "}
          <b>24 hours</b> of its creation. It acts at once on this one VM; the
          recovery job waits 15–30 minutes and never touches Error. SuperAdmin
          only. Offered only when the server marks this VM retryable (Error left
          by the build, order still owing it, still inside the 24h window); the
          server still checks everything again on submit and a refusal comes
          back as the error message.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">By status</p>
          <ul className="space-y-1 text-muted-foreground">
            {/* Retry no longer accepts Pending or Provisioning — the server returns 409
                RESOURCE_STATUS_NOT_RETRYABLE for both (2026-09-28). Restore these two bullets if
                that changes.
            <li>
              · <b>Pending</b>, order Paid: re-runs the step after payment for
              the whole order — resources to Provisioning, billing cycle
              opened, build queued — in one transaction.
            </li>
            <li>
              · <b>Provisioning</b>: queues the build again; nothing is
              written.
            </li>
            */}
            <li>
              · <b>Error</b> left by the build, order Paid / Processing /
              Complete / Failed and paid: in one save, the VM and its failed
              disks and networks go back to Provisioning, a failed order goes
              back to Processing and the error the customer sees is cleared;
              then the build is queued at once.
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">The build job then</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              · <b>provider id recorded</b>: re-checks that machine, never
              creates another
            </li>
            <li>
              · <b>provider id recorded, machine gone</b>: tick{" "}
              <b>Drop this machine id</b> in this dialog. The server asks the
              infrastructure and drops the id only when it answers that the
              machine does not exist; the job then creates a new machine. The
              dropped id is kept on the audit row
            </li>
            <li>
              · <b>no provider id</b>: claims the create call, then asks the
              provider to create the machine
            </li>
            <li>
              · the claim lets only <b>one</b> run send the create request, even
              with the recovery job, a Hangfire retry or a double click. It is
              cleared when the call ends, and lapses after 10 minutes if a run
              died holding it
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">Before an Error VM with no provider id</p>
          <p className="text-muted-foreground">
            WiCloud cannot tell whether its last create request was carried out,
            so the machine may exist. This dialog warns, and Retry waits until
            you confirm with the infrastructure team that no machine exists; if
            one does, use Recover VM from infrastructure. Customers cannot retry
            such a VM themselves — only one with a provider id.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">It refuses a VM that</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              · has run before (its Error came from a later step, such as a
              failed reinstall)
            </li>
            <li>
              · was deleted, has no order, or is not on a new-service order
            </li>
            <li>· failed in something other than the build</li>
            <li>· was created more than 24 hours ago</li>
            <li>
              · nobody pays for any more (order cancelled or refunded, cycle
              cancelled or missing), or has no billing cycle at all — with no
              expiry date the expiry scan would suspend it as soon as it runs
            </li>
            <li>
              · has a refunded payment on its order, or no payment that shows
              the order was paid (the same rule Recover uses to settle an order)
            </li>
            <li>
              · has no provider id while a run holds its create claim — the
              message says when the claim lapses
            </li>
            {/* Pending is no longer offered at all (only Error is retryable, 2026-09-28); restore
                this bullet if Pending comes back.
            <li>· is Pending on an order that is not Paid</li>
            */}
            <li>
              · is in any other status (Pending, Provisioning, Running, Stopped,
              Suspended…)
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">It never</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· extends the expiry — correct it by hand if days were lost</li>
            <li>· emails the customer, moves money or changes a price</li>
          </ul>
          <p className="text-muted-foreground">
            Every retry writes an audit row the customer does not see.
          </p>
        </div>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a
            href="/docs/retry-provisioning.html"
            target="_blank"
            rel="noreferrer"
          >
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
